import { useState, useEffect, useRef } from "react";
import "./SearchMatchesPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faStar, faSearch } from "@fortawesome/free-solid-svg-icons";
import AnalysisRenderer from "../components/analysis-editor/AnalysisRenderer";
import MatchStatsModal from "./MatchStatsModal";
import API from "../services/api";

function SearchMatchesPage() {
  // --- 1. États (States) ---
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem("user")));
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [openedAnalysis, setOpenedAnalysis] = useState(null);
  const [showVipModal, setShowVipModal] = useState(false);
  const [openedMatch, setOpenedMatch] = useState(null);
  
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalType, setLoginModalType] = useState("favorite");
  const [favorites, setFavorites] = useState([]);

  // Référence pour toujours avoir la valeur la plus récente de 'search' dans l'intervalle
  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  // --- 2. Fonctions de requêtes API ---
  const loadMatches = async () => {
    const currentSearch = searchRef.current;
    if (!currentSearch.trim()) return;
    try {
      const res = await API.get(`/matches/search?q=${currentSearch}`);
      setMatches(res.data);
    } catch (error) {
      console.error("Erreur lors du rafraîchissement des matchs:", error);
    }
  };

  const handleSearch = async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const res = await API.get(`/matches/search?q=${search}`);
      setMatches(res.data);
    } catch (error) {
      console.error("Erreur lors de la recherche manuelle:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- 3. Effets (Effects) ---
  
  // Debounce de la recherche automatique + Gestion de l'intervalle dynamique
  useEffect(() => {
    if (!search.trim()) {
      setMatches([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Attendre 400ms après la fin de la frappe avant de lancer la recherche initiale
    const timer = setTimeout(async () => {
      await loadMatches();
      setLoading(false);
    }, 400);

    // Mettre en place le rafraîchissement toutes les 15 secondes
    const interval = setInterval(() => {
      loadMatches();
    }, 15000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [search]);

  // --- 4. Actions Utilisateur & Utilitaires ---
  const openStats = (match) => {
    setSelectedMatch(match);
    setShowStatsModal(true);
  };

  const closeStats = () => {
    setShowStatsModal(false);
    setSelectedMatch(null);
  };

  const togglePredictions = (id) => {
    setOpenedMatch(openedMatch === id ? null : id);
  };

  const openAnalysis = (match) => {
    const token = localStorage.getItem("token");
    const freshUser = JSON.parse(localStorage.getItem("user"));

    if (match.accessLevel !== "vip") {
      setOpenedAnalysis(match);
      return;
    }
    if (!token) {
      setLoginModalType("vip");
      setShowLoginModal(true);
      return;
    }
    if (!freshUser?.isVip) {
      setShowVipModal(true);
      return;
    }
    setOpenedAnalysis(match);
  };

  const toggleFavorite = async (matchId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoginModalType("favorite");
      setShowLoginModal(true);
      return;
    }
    try {
      const response = await API.post(`/users/favorites/${matchId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.favorite) {
        setFavorites((prev) => [...prev, matchId]);
      } else {
        setFavorites((prev) => prev.filter((id) => id !== matchId));
      }
    } catch (error) {
      console.error("Erreur favoris:", error);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "reussi":
      case "won":
        return "prediction-success";
      case "echec":
      case "lost":
        return "prediction-failed";
      case "en_cours":
      case "pending":
        return "prediction-pending";
      default:
        return "";
    }
  };

  const renderPrediction = (prediction) => (
    <div className={`prediction ${getStatusClass(prediction?.status)}`}>
      {prediction?.value && prediction.value.trim() !== "" ? prediction.value : "Aucun pronostic"}
    </div>
  );

  const liveStatuses = ["1H", "2H", "ET", "BT", "P", "LIVE", "HT"];

  // --- 5. Rendu (JSX) ---
  return (
    <div className="search-page">
      <h1>Recherche Matchs</h1>

      <div className="search-box">
        <input
          type="text"
          placeholder="Rechercher un pays, une équipe ou un championnat..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button onClick={handleSearch}>
          <FontAwesomeIcon icon={faSearch} style={{ marginRight: 6 }} />
          <span>Rechercher</span>
        </button>
      </div>

      {loading &&
<div className="conteneur-grid">
        <div className="grid">
          {[1, 2, 3].map((n) => (
            <div key={n} className="card">
              <div className="card__title skeleton"></div>
              <div className="card__meta skeleton"></div>
              <div className="card__buttons">
                <button className="skeleton"></button>
                <button className="skeleton"></button>
                <button className="skeleton"></button>
              </div>
            </div>
          ))}
        </div>
      </div>       }

      {!loading && search && matches.length === 0 && (
        <div className="no-match">Aucun match trouvé</div>
      )}

      <div className="search-results">
        {matches.map((match) => {
          const homeRedCards = match.events
            ? match.events.filter(
                (e) => e.team === match.homeTeam && e.type === "Card" && (e.detail === "Red Card" || e.detail === "Second Yellow Card")
              ).length
            : 0;

          const awayRedCards = match.events
            ? match.events.filter(
                (e) => e.team === match.awayTeam && e.type === "Card" && (e.detail === "Red Card" || e.detail === "Second Yellow Card")
              ).length
            : 0;

          return (
            <div key={match._id} className="search-match-card">
              <div className="favorite-btn" onClick={() => toggleFavorite(match._id)}>
                <FontAwesomeIcon icon={faStar} style={{ color: favorites.includes(match._id) ? "gold" : "gray" }} />
              </div>

              <div className="league">{match.country} - {match.league}</div>

              <div className="teams">
                <div className="team">
                  {match.homeTeam}
                  {homeRedCards > 0 && <span className="red-card-badge"> 🟥 {homeRedCards}</span>}
                </div>
                <div className="vs">-</div>
                <div className="team">
                  {awayRedCards > 0 && <span className="red-card-badge">{awayRedCards} 🟥 </span>}
                  {match.awayTeam}
                </div>
              </div>

              <div className="match-time">
                📅 {new Date(match.matchDate).toLocaleString()}
              </div>

              <div className="statut-conteneur">
                <div 
                  className={`match-live-box ${match.scoreEffectUntil > Date.now() ? match.scoreEffect : ""}`} 
                  onClick={() => openStats(match)}
                >
                  <div className={`match-status ${match.liveStatus}`}>
                    {liveStatuses.includes(match.liveStatus) && match.liveStatus !== "HT"
                      ? `🔴 ${match.elapsed}'`
                      : match.liveStatus === "HT"
                      ? "🟡 MT"
                      : match.liveStatus === "FT"
                      ? "⚫ FT"
                      : "🟢 NS"}
                  </div>
                  
                  {match.liveStatus !== "NS" && (
                    <div className="live-score">
                      <span>{match.goalsHome ?? 0}</span> - <span>{match.goalsAway ?? 0}</span>
                    </div>
                  )}

                  {["HT", "2H", "FT"].includes(match.liveStatus) && (
                    <div className="halftime-score">
                      MT : {match.htGoalsHome} - {match.htGoalsAway}
                    </div>
                  )}

                  <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 8, marginBottom: 3, color: "var(--orange)" }} />
                </div>
              </div>

              {match.accessLevel === "vip" && !user?.isVip ? (
                <div className="predictions">
                  <div className="prediction vip-prediction">🔒 Pronostic VIP</div>
                </div>
              ) : (
                <button className="show-predictions-btn" onClick={() => togglePredictions(match._id)}>
                  {openedMatch === match._id ? "Masquer" : "Voir les pronostics"}
                  <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 8 }} />
                </button>
              )}

              {openedMatch === match._id && (
                <div className="predictions-box">
                  <div><strong>Résultat :</strong> <span>{renderPrediction(match.predictions?.resultat)}</span></div>
                  <div><strong>Double Chance :</strong> <span>{renderPrediction(match.predictions?.doubleChance)}</span></div>
                  <div><strong>BTTS :</strong> <span>{renderPrediction(match.predictions?.btts)}</span></div>
                  <div><strong>Plus / Moins :</strong> <span>{renderPrediction(match.predictions?.plusMoins)}</span></div>
                  <div><strong>Mi-temps + Buts :</strong> <span>{renderPrediction(match.predictions?.mtPlus)}</span></div>
                  <div><strong>Mi-temps / Fin :</strong> <span>{renderPrediction(match.predictions?.mtFin)}</span></div>
                  <div><strong>Score Exact MT :</strong> <span>{renderPrediction(match.predictions?.scoreMt)}</span></div>
                  <div><strong>Score Exact FT :</strong> <span>{renderPrediction(match.predictions?.scoreExact)}</span></div>
                </div>
              )}

              <div className="confidence-container">
                <div className="confidence-header">
                  <span>Fiabilité : </span>
                  <strong>{match.confidence || 0}%</strong>
                </div>
                <div className="confidence-bar">
                  <div
                    className="confidence-fill"
                    style={{
                      width: `${match.confidence || 0}%`,
                      background: match.confidence >= 80 ? "var(--barre-progress)" : match.confidence >= 60 ? "var(--encours)" : "var(--danger)"
                    }}
                  />
                </div>
              </div>

              <div className="analysis-btn">
                <button className="open-analysis-btn" onClick={() => openAnalysis(match)}>
                  {match.accessLevel === "vip" && !user?.isVip ? "🔒 Débloquer l'analyse VIP" : "Voir l'analyse complète"}
                  <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 8 }} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* --- MODAL ANALYSE --- */}
      {openedAnalysis && (
        <div className="analysis-modal">
          <div className="analysis-modal-content">
            <button className="close-analysis" onClick={() => setOpenedAnalysis(null)}>✕</button>
            <h1>{openedAnalysis.homeTeam} vs {openedAnalysis.awayTeam}</h1>      
            
            <div className="analysis-modal-time">      
              <p>🕒 {new Date(openedAnalysis.matchDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              <div className="fullscreen-score">
                <div>{openedAnalysis.goalsHome} - {openedAnalysis.goalsAway}</div>
                <div>MT : {openedAnalysis.htGoalsHome} - {openedAnalysis.htGoalsAway}</div>
              </div>
            </div>
            
            <AnalysisRenderer blocks={openedAnalysis.analysisBlocks} />
          </div>
        </div>
      )}

      {/* --- MODAL LOGIN / REQUIS --- */}
      {showLoginModal && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <div className="login-modal-icon">{loginModalType === "favorite" ? "⭐" : "📊"}</div>
            <h2>{loginModalType === "favorite" ? "Ajouter aux favoris" : "Connexion requise"}</h2>
            <p>{loginModalType === "favorite" ? "Connectez-vous pour suivre vos matchs favoris." : "Cette analyse est réservée aux membres VIP."}</p>
            <div className="login-modal-buttons">
              <button className="btn-login" onClick={() => window.location.href = "/login"}>Se connecter</button>
              <button className="btn-register" onClick={() => window.location.href = "/register"}>Créer un compte</button>
            </div>
            <button className="close-modal" onClick={() => setShowLoginModal(false)}>Fermer</button>
          </div>
        </div>
      )}

      {/* --- MODAL VIP REQUIS --- */}
      {showVipModal && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <div className="login-modal-icon">🔒</div>
            <h2>Analyse VIP</h2>
            <p>Cette analyse est réservée aux membres VIP. Passez au VIP pour y accéder.</p>
            <div className="login-modal-buttons">
              <button className="btn-login" onClick={() => window.location.href = "/vip"}>Devenir VIP</button>
            </div>
            <button className="close-modal" onClick={() => setShowVipModal(false)}>Fermer</button>
          </div>
        </div>
      )}

      {/* --- MODAL STATISTIQUES --- */}
      {showStatsModal && selectedMatch && (
        <MatchStatsModal match={selectedMatch} onClose={closeStats} />
      )}
    </div>
  );
}

export default SearchMatchesPage;